import { create } from "zustand";
import { persist, createJSONStorage } from "zustand/middleware";
import { MasterKeyManager } from "@/shared/lib/crypto/masterKey";
import { clearAllWorkspaceKeys } from "@/shared/lib/vfs-crypto/keystore";
import { sessionBlobCache } from "@/features/context-explorer/lib/sessionBlobCache";
import { clearAllEphemeralKeys } from "@/features/distributed-search/crypto/ephemeralKeystore";

export interface User {
  id: number;
  email: string;
  name?: string;
  role: "admin" | "curator" | "basic" | "slack_bot" | "limited";
  is_active: boolean;
  is_verified: boolean;
  is_anonymous_user?: boolean;
  profile_picture_url?: string | null;
}

interface AuthState {
  user: User | null;
  token: string | null;
  isLoading: boolean;
  /**
   * [ZKPA] Login password kept in memory only — never persisted to localStorage.
   * Required by useMasterKey to decrypt the Master Key each session.
   * Cleared on logout alongside MasterKeyManager.clear().
   */
  sessionPassword: string | null;
  setUser: (user: User, token?: string) => void;
  setSessionPassword: (password: string | null) => void;
  setLoading: (loading: boolean) => void;
  logout: () => void;
  isAuthenticated: () => boolean;
  isAdmin: () => boolean;
}

/**
 * Auth store — persisted to localStorage.
 * Replaces Next.js server-side session/cookie auth pattern.
 */
export const useAuthStore = create<AuthState>()(
  persist(
    (set, get) => ({
      user: null,
      token: null,
      isLoading: true,
      sessionPassword: null,

      setUser: (user, token) =>
        set({ user, token: token ?? get().token, isLoading: false }),

      // [ZKPA] Store login password in memory so useMasterKey can decrypt MK.
      // Never call this from outside the login flow.
      setSessionPassword: (password) => set({ sessionPassword: password }),

      setLoading: (loading) => set({ isLoading: loading }),

      // TASK-041-4 (SOL-041 §5) — delta: local state still clears
      // synchronously/immediately (unchanged UX/timing for all 6 call
      // sites: hooks/useAuth.ts, services/api.ts's 401 interceptor,
      // AccountPopover.tsx, AppHealthBanner.tsx, NewTenantModal.tsx,
      // AccessRestrictedPage.tsx), plus a fire-and-forget POST to the
      // session-kms-service logout endpoint (backend TASK-017-3) so the
      // server-side session/key leases actually get revoked instead of
      // just going stale.
      //
      // Deviation from the SOL-041 §5 sketch (`api.post('auth/logout')` via
      // the `ky` singleton in services/api.ts) — verified against the real
      // call graph, not assumed:
      //  1. services/api.ts already does `import { useAuthStore } from
      //     "@/shared/stores/authStore"` (Bearer-token injection + the 401
      //     interceptor below). Importing `api` back into this file would
      //     be a circular module dependency.
      //  2. That same 401 interceptor calls `useAuthStore.getState().logout()`.
      //     If the fire-and-forget request went through the `api` (ky)
      //     instance and itself came back 401 (token already cleared by the
      //     time it's sent), `api`'s `afterResponse` hook would call
      //     `logout()` again -> infinite loop. A bare `fetch()` never
      //     touches that hook, so this can't happen — matches the D7
      //     constraint ("401 interceptor không gọi lại logout API") without
      //     needing to special-case that call site at all.
      // Token is read BEFORE clearing so the outgoing request still carries
      // whatever session it's meant to revoke.
      logout: () => {
        // [ZKPA] Clear all key material from memory FIRST
        MasterKeyManager.clear();
        // Wipe ephemeral E2E file-delivery private keys (IndexedDB, TASK-UI-VFS-004-1)
        void clearAllEphemeralKeys();
        // Wipe cached VFS workspace keys (memory + IndexedDB, TASK-UI-VFS-003-2)
        void clearAllWorkspaceKeys();
        // Revoke decrypted distributed-search blob URLs (TASK-UI-VFS-004-3)
        sessionBlobCache.clear();
        
        const token = get().token;
        set({ user: null, token: null, isLoading: false, sessionPassword: null });
        const apiBase = import.meta.env.VITE_API_BASE_URL ?? "/api";
        fetch(`${apiBase}/auth/logout`, {
          method: "POST",
          credentials: "include",
          headers: token ? { Authorization: `Bearer ${token}` } : undefined,
        }).catch(() => {
          // fire-and-forget — never throw out of a store action
        });
      },

      isAuthenticated: () => get().user !== null,
      isAdmin: () => {
        const role = get().user?.role;
        return role === "admin" || role === "system_admin" || role === "workspace_admin";
      },
    }),
    {
      name: "vnp-auth",
      storage: createJSONStorage(() => localStorage),
      // sessionPassword intentionally excluded — must never be written to localStorage
      partialize: (state) => ({ user: state.user, token: state.token }),
    },
  ),
);
